import React, {FC, useEffect, useState} from 'react';
import cs from 'classnames';
import {Rating as ReactRating, RatingProps} from 'react-simple-star-rating';
import {Typography} from 'components/Typography';

import style from './style.module.scss';
import {RATING_ITEM_COLOR, RATING_ITEMS_AMOUNT, RATING_TYPE} from './constants';
import Icon from 'components/Icon';
import {ClientOnly} from 'components/ClientOnly';
import {formatRating} from 'core/helpers';
import isNumber from 'lodash/isNumber';

export type TRatingType = keyof typeof RATING_TYPE;

interface IProps extends RatingProps {
    isReadOnly?: boolean;
    rate?: number;
    onClick?: (rate: number) => void;
    className?: string;
    type?: TRatingType;
    infoText?: string | null;
    defaultRate?: number;
    withTitle?: boolean;
    classNames?: Partial<{
        ratingIcon: string;
        ratingIconsContainer: string;
        ratingIconsWrapper: string;
        ratingInfo: string;
        ratingTitle: string;
        ratingTooltip: string;
    }>;
}

export const Rating: FC<IProps> = ({
    isReadOnly,
    rate,
    onClick,
    className,
    type = RATING_TYPE.default,
    infoText,
    withTitle = true,
    classNames = {},
    defaultRate = 0,
    ...rest
}) => {
    const [rating, setRating] = useState<number>(defaultRate);

    useEffect(() => {
        setRating(rate || defaultRate);
    }, [defaultRate, rate]);

    const handleRating = (rateCount: number) => {
        setRating(rateCount);

        if (onClick) {
            onClick(rateCount);
        }
    };

    if (type === RATING_TYPE.circle) {
        return (
            <div className={cs(style.ratingCircle, 'rating-circle')}>
                {Array.from({length: RATING_ITEMS_AMOUNT}, (_, index) => (
                    <div
                        className={cs(style.circleItem, 'rating-circle-item', {
                            [style.active]: index < Math.floor(rating),
                        })}
                        key={index}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={cs(style.rating, className)}>
            {isReadOnly && withTitle && isNumber(rate) && (
                <Typography className={cs('rate-value', classNames.ratingTitle)} variant={'title-24-m'}>
                    {formatRating(rate)}
                </Typography>
            )}
            <div className={cs(style.wrapper, 'rating-icons-wrapper', classNames?.ratingIconsWrapper)}>
                <ClientOnly>
                    <ReactRating
                        {...rest}
                        allowFraction={isReadOnly}
                        className={cs(
                            style.ratingIconsContainer,
                            'rating-icons-container',
                            classNames?.ratingIconsContainer,
                        )}
                        emptyColor={RATING_ITEM_COLOR.empty}
                        emptyIcon={
                            <Icon
                                className={cs('rating-icon', style.ratingIcon, classNames.ratingIcon)}
                                icon={'star'}
                            />
                        }
                        fillColor={RATING_ITEM_COLOR.filled}
                        fillIcon={
                            <Icon
                                className={cs('rating-icon', style.ratingIcon, classNames.ratingIcon)}
                                icon={'star'}
                            />
                        }
                        initialValue={rating}
                        onClick={handleRating}
                        readonly={isReadOnly}
                        tooltipClassName={cs(style.ratingTooltip, classNames.ratingTooltip)}
                        titleSeparator={'из'}
                    />
                </ClientOnly>
                {Boolean(infoText) && (
                    <Typography
                        className={cs(style.ratingInfo, 'rating-info-text', classNames.ratingInfo)}
                        color={'gray60'}
                        variant={'p-medium'}
                    >
                        {infoText}
                    </Typography>
                )}
            </div>
        </div>
    );
};
